import { Search, Edit, Trash2, Clock, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import useApi from "../../hooks/useApi";
import EditCashCustomerList from "./EditCashCustomerList";
import { countries } from "./data";
import useKeyboardNavigation from "../../hooks/useKeyboardNavigation";
import AccountTimelineDrawer from "./CashAccountTimeline";
import checkIfUserHasAccess from "../common/UserCheckAccess";

const CashCustomerList = ({
  handleCustomerSelect,
  customer = {},
  type = "customer",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [customers, setCustomers] = useState([]);
  const [activeTab, setActiveTab] = useState("list");
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(null);

  const isSupplier = type === "supplier";
  const entityLabel = isSupplier ? "Supplier" : "Customer";

  const [formData, setFormData] = useState({
    primaryPhone: "",
    primaryCountryCode: "+971",
    altCountryCode: "+971",
    alternatePhone: "",
    whatsappPrimary: false,
    whatsappAlternate: false,
    title: "",
    contactName: "",
    email: "",
    comment: "",
    patronType: "Patron",
    areaCode: "Area 1",
    creditExpiry: "",
    creditLimit: "",
    trn: "",
    invoice: false,
    city: "",
    country: "UAE",
    territory: "Dubai",
    location: "",
    streetAddress: "",
  });
  const [errors, setErrors] = useState({});

  const COMPANY_CODE = localStorage?.getItem("companyCode") || "-";
  const DEPARTMENT_NO =
    localStorage.getItem("currentSelectedDepartment") || "MAIN";
  const API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const CURRENT_USER_OBJECT = localStorage.getItem("currentUser")
    ? JSON.parse(localStorage.getItem("currentUser"))
    : "";
  const CURRENT_USER = CURRENT_USER_OBJECT[0];
  const BASE_URL = API_PATHS[0].API_CONFIG || "https://cubixweberp.com:213";
  const { request, loading } = useApi();

  const fetchCustomerList = async (freetext = "-") => {
    const listEndpoint = isSupplier ? "WALKINLISTSUPP" : "WALKINLIST";
    const url = `${BASE_URL}/api/CRMAccountList/${COMPANY_CODE}/${listEndpoint}/${DEPARTMENT_NO}/ALL/-/${freetext}`;

    try {
      const response = await request({
        url: url,
        method: "GET",
        timeout: 10000,
      });
      console.log(`Cash ${entityLabel}s Response`, response);
      setCustomers(response || []);
    } catch (err) {
      console.error(`Error loading cash ${entityLabel}s:`, err);
      setCustomers([]);
    }
  };

  const handleStatusUpdate = async (item) => {
    try {
      const payload = [
        {
          cmpcode: COMPANY_CODE,
          doc_no: item.AutoId.toString(),
          status: "REQUESTED",
          evaluationcomments: "Requesting For Credit Creation",
          userid: CURRENT_USER?.user,
          deptno: DEPARTMENT_NO,
          doctype: "CUSTCREATIONREQ",
        },
      ];

      const url = `${BASE_URL}/api/CRMDocStatusUpdate`;

      await request({
        url,
        method: "POST",
        data: payload,
        config: { headers: { "Content-Type": "application/json" } },
      });

      alert("Request submitted.");
      fetchCustomerList(searchTerm.trim() === "" ? "-" : searchTerm.trim());
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status.");
    }
  };

  const handleShowTimeline = (e, item) => {
    e.stopPropagation();
    setSelectedAccountId(item.AutoId);
    setIsTimelineOpen(true);
  };

  const { selectedIndex, rowRefs } = useKeyboardNavigation({
    list: customers,
    onEnter: handleCustomerSelect,
    isActive: activeTab === "list" && !loading,
  });

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const freetext = searchTerm.trim() === "" ? "-" : searchTerm.trim();
      fetchCustomerList(freetext);
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm, type]);

  const parsePhoneNumber = (fullNumber) => {
    if (!fullNumber) return { code: "+971", number: "" };
    const cleaned = fullNumber.replace(/\s+/g, "");
    const matched = countries?.find((c) => cleaned.startsWith(c.code));

    if (matched) {
      return {
        code: matched.code,
        number: cleaned.replace(matched.code, ""),
      };
    }
    return { code: "+971", number: fullNumber };
  };

  const formatBalance = (balance) =>
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(balance);

  const handleEdit = async (customer) => {
    try {
      const autoid = customer?.AutoId;
      const url = `${BASE_URL}/api/CRMAccountInfo/${COMPANY_CODE}/Single_BillInfoWalkin/-/${autoid}`;
      const response = await request({
        url,
        method: "GET",
        timeout: 10000,
      });

      if (response) {
        const customerData = response[0];
        const { code: parsedTelCode, number: parsedTelNumber } =
          parsePhoneNumber(customerData.phone);
        const { code: parsedAltCode, number: parsedAltNumber } =
          parsePhoneNumber(customerData.Alt_Phone);

        console.log(`${entityLabel} data to EDIT`, customerData);
        setEditingCustomer(customer);

        setFormData({
          primaryPhone: parsedTelNumber || "",
          primaryCountryCode: parsedTelCode || "+971",
          altCountryCode: parsedAltCode || "+971",
          alternatePhone: parsedAltNumber || "",
          whatsappPrimary: customerData.PriWhatsapp === "True",
          whatsappAlternate: customerData.OtWhatsapp === "True",
          title: customerData.accdesc?.split(" ")[0] || "",
          contactName: customerData.accdesc,
          email: customerData.email || "",
          comment: customerData.remark || "",
          patronType: customerData.patrontype || "Individual",
          areaCode: customerData.areacode || "Area 1",
          creditExpiry: customerData.duedays?.toString() || "",
          creditLimit: customerData.climit?.toString() || "",
          trn: customerData.TRN || "",
          invoice: customerData.invoice || false,
          city: customerData.address1 || "",
          country: customerData.country || "UAE",
          territory: customerData.adr3 || "Dubai",
          location: customerData.GPSCorditate || "",
          streetAddress: customerData.address3 || "",
        });

        setActiveTab("edit");
        setErrors({});
      } else {
        console.error("Failed to fetch detailed info");
      }
    } catch (error) {
      console.error("Error fetching info:", error);
    }
  };

  const handleDelete = async (customer) => {
    if (
      window.confirm(`Are you sure you want to delete ${customer.DESCRIPTION}?`)
    ) {
      console.log(`Deleting ${entityLabel}:`, customer);
      fetchCustomerList(searchTerm.trim() === "" ? "-" : searchTerm.trim());
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.contactName.trim()) {
      newErrors.contactName = "Contact name is required";
    }

    if (!formData.primaryPhone.trim()) {
      newErrors.primaryPhone = "Primary phone number is required";
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (formData.creditLimit && parseFloat(formData.creditLimit) < 0) {
      newErrors.creditLimit = "Credit limit cannot be negative";
    }

    if (formData.creditExpiry && parseInt(formData.creditExpiry) < 0) {
      newErrors.creditExpiry = "Due days cannot be negative";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      const mappedData = {
        cmpcode: COMPANY_CODE,
        mod: "M",
        cscode: editingCustomer?.ACCOUNT || "00000000",
        ac_code: "0000",
        type: "W",
        cs_des: `${formData.contactName}`.trim(),
        balance_open: 0,
        dr: 0.0,
        cr: 0.0,
        fc: "AED",
        fdr: 0.0,
        fcr: 0.0,
        divcode: "-",
        keywords: "-",
        adr1: formData.city || "-",
        adr2: formData.streetAddress || "-",
        adr3: formData.territory || "-",
        adr4: formData.contactName || "-",
        adr5: "-",
        fax: "-",
        tel: `${formData.primaryCountryCode || ""} ${
          formData.primaryPhone || ""
        }`.replace(/\s+/g, ""),
        mobile: `${formData.altCountryCode || ""} ${
          formData.alternatePhone || ""
        }`.replace(/\s+/g, ""),
        salesman: CURRENT_USER?.sales_man || "John Doe",
        areacode: formData.areaCode || "QUSAIS",
        email: formData.email || "-",
        remarks: formData.comment || "-",
        country: formData.country || "UAE",
        state: "-",
        crlimit: parseFloat(formData.creditLimit) || 0.0,
        duedays: parseInt(formData.creditExpiry) || 30,
        terms: "-",
        crmethod: "Credit Block",
        cntrlcode: "-",
        avtive: "Y",
        colourcode: "-",
        price_cat: "-",
        grade: "A",
        vatregno: formData.trn || "-",
        shippingadr: "-",
        create_user: "admin",
        discount: 0,
        margin: 0,
        GPSCorditate: formData.location || "-",
        PriWhatsapp: formData.whatsappPrimary ? "True" : "False",
        OtWhatsapp: formData.whatsappAlternate ? "True" : "False",
        QtnStat: "-",
        InvStat: "-",
        RetnStat: "-",
        UserTrn: "-",
        UseForDel: "-",
        custtype: isSupplier ? "s" : "c",
        autoid: editingCustomer?.AutoId,
        patron: "-",
        patrontype: formData.patronType || "Individual",
        officenumber: "-",
        regno: "-",
        dumpfield: "-",
      };

      try {
        console.log(`Saving ${entityLabel}:`, mappedData);
        const url = `${BASE_URL}/api/CRMAcmastCRM`;
        const response = await request({
          url: url,
          method: "POST",
          data: [mappedData],
          config: {
            headers: {
              "Content-Type": "application/json",
            },
          },
          timeout: 5000,
        });
        console.log(`${entityLabel} data Saved`, response);
        setActiveTab("list");
        setEditingCustomer(null);
        fetchCustomerList(searchTerm.trim() === "" ? "-" : searchTerm.trim());
      } catch (error) {
        console.error(`Error saving ${entityLabel}:`, error);
      }
    }
  };

  const handleCancel = () => {
    setActiveTab("list");
    setEditingCustomer(null);
    setFormData({
      primaryPhone: "",
      primaryCountryCode: "+971",
      altCountryCode: "+971",
      alternatePhone: "",
      whatsappPrimary: false,
      whatsappAlternate: false,
      title: "",
      contactName: "",
      email: "",
      comment: "",
      patronType: "Patron",
      areaCode: "Area 1",
      creditExpiry: "",
      creditLimit: "",
      trn: "",
      invoice: false,
      city: "",
      country: "UAE",
      territory: "Dubai",
      location: "",
      streetAddress: "",
    });
    setErrors({});
  };

  const updateFormData = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="bg-gray-50">
      {/* Timeline Drawer */}
      <AccountTimelineDrawer
        isOpen={isTimelineOpen}
        onClose={() => setIsTimelineOpen(false)}
        accountId={selectedAccountId}
        entityLabel={entityLabel}
        DEPARTMENT={DEPARTMENT_NO}
      />

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <nav className="flex lg:flex-row flex-col items-center justify-between px-6">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab("list")}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "list"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Cash {entityLabel}s List
            </button>
            {activeTab === "edit" && (
              <button
                onClick={() => setActiveTab("edit")}
                className="py-3 px-1 border-b-2 border-blue-500 text-blue-600 font-medium text-sm"
              >
                Edit {entityLabel}: {editingCustomer?.DESCRIPTION}
              </button>
            )}
          </div>

          {activeTab === "list" ? (
            <div className="flex items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={`Search cash ${entityLabel.toLowerCase()}s...`}
                  value={searchTerm}
                  autoFocus
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64 pl-9 pr-4 py-2 border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white shadow-sm transition-all placeholder:text-gray-400"
                />
              </div>
            </div>
          ) : (
            <></>
          )}
        </nav>
      </div>

      {/* List Tab Content */}
      {activeTab === "list" && (
        <div className="lg:p-6 p-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <thead className="bg-gray-200 border-b border-gray-200 sticky top-0 z-10">
                  <tr>
                    <th className="w-1/12 px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      AutoId
                    </th>
                    <th className="w-3/12 px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Description
                    </th>
                    <th className="w-2/12 px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Phone
                    </th>
                    {checkIfUserHasAccess("CT") && (
                      <th className="w-2/12 px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Balance
                      </th>
                    )}
                    <th className="w-2/12 px-5 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                    <th className="w-2/12 px-5 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                  </tr>
                </thead>
              </table>
            </div>

            <div className="max-h-96 overflow-y-auto scrollbar-thin">
              <table className="w-full table-fixed">
                <tbody className="divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <svg
                            className="animate-spin h-8 w-8 text-blue-500 mb-3"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8z"
                            ></path>
                          </svg>
                          <p className="text-sm text-gray-500">
                            Loading cash {entityLabel.toLowerCase()}s...
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : customers?.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="py-12 text-center">
                        <Search className="w-12 h-12 text-gray-300 mb-3 mx-auto" />
                        <p className="text-lg font-medium text-gray-900 mb-1">
                          No cash {entityLabel.toLowerCase()}s found
                        </p>
                        <p className="text-sm text-gray-500">
                          Try a different search term
                        </p>
                      </td>
                    </tr>
                  ) : (
                    customers?.map((item, index) => (
                      <tr
                        ref={rowRefs.current[index]}
                        key={index}
                        onClick={() => handleCustomerSelect(item)}
                        className={`hover:bg-blue-50 cursor-pointer ${
                          index === selectedIndex ? "bg-gray-100" : ""
                        }`}
                      >
                        <td className="w-1/12 px-5 py-3 text-sm font-medium text-gray-900 cursor-pointer">
                          {item.AutoId}
                        </td>
                        <td className="w-3/12 px-5 py-3 text-sm text-left text-gray-700 cursor-pointer">
                          {item.DESCRIPTION}
                        </td>
                        <td className="w-2/12 px-5 py-3 text-sm text-left text-gray-700 cursor-pointer">
                          {item.Contact_No}
                        </td>
                        {checkIfUserHasAccess("CT") && (
                          <td
                            className={`w-2/12 px-5 py-3 text-sm text-right font-medium cursor-pointer ${
                              item.BALANCE > 0
                                ? "text-green-600"
                                : item.BALANCE < 0
                                  ? "text-red-600"
                                  : "text-gray-700"
                            }`}
                          >
                            {formatBalance(item.BALANCE)}
                          </td>
                        )}
                        <td className="w-2/12 px-5 py-3 text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(item);
                              }}
                              className="px-2 py-2 text-xs flex items-center gap-1 font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              title={`Edit ${entityLabel}`}
                            >
                              <Edit className="w-3 h-3" /> Edit
                            </button>
                            <button
                              onClick={(e) => handleShowTimeline(e, item)}
                              className="px-2 py-2 text-xs flex items-center gap-1 font-medium bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                              title="View Timeline"
                            >
                              <Clock className="w-3 h-3" /> Timeline
                            </button>
                          </div>
                        </td>
                        <td className="w-2/12 px-5 py-3 text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusUpdate(item);
                            }}
                            disabled={
                              item.Status === "ACCEPTED" ||
                              item.Status === "REQUESTED"
                            }
                            className={`px-3 py-1 text-xs rounded-lg font-medium transition 
                        ${
                          item.Status === "-"
                            ? "bg-blue-600 text-white hover:bg-blue-700"
                            : item.Status === "REJECTED"
                              ? "bg-red-600 text-white hover:bg-red-700"
                              : "bg-gray-300 text-gray-600 cursor-not-allowed"
                        }`}
                          >
                            {item.Status === "-" && "Request to Credit"}
                            {item.Status === "REQUESTED" && "Requested"}
                            {item.Status === "ACCEPTED" && "ACCEPTED"}
                            {item.Status === "REJECTED" && "Request Again"}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tab Content */}
      {activeTab === "edit" && (
        <EditCashCustomerList
          handleCancel={handleCancel}
          handleSave={handleSave}
          loading={loading}
          formData={formData}
          updateFormData={updateFormData}
          errors={errors}
        />
      )}
    </div>
  );
};

export default CashCustomerList;
